import { Archive, ArrowRightLeft, DoorOpen, Download, FileSpreadsheet, Laptop, Package, Plug, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Alerta, Boton, Cargando, Panel } from '../../components/kit'
import { useAuth } from '../../contexts/AuthProvider'
import { useDescargarPlanilla, useResumen, useSolicitudes } from '../../queries/useInventario'
import { Revisar } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { PLANCHETA, REVISAR, TIPO } from '../../utils/inventario'

const numero = (n: number) => n.toLocaleString('es-CL')

/** Portada: para quien administra bienes, el avance de la revisión; para el resto, lo suyo. */
export default function Inicio() {
  const { user, puede } = useAuth()
  const nombre = user?.persona?.nombre_corto || user?.first_name || user?.username
  return puede('administra_bienes') ? <InicioInventario nombre={nombre} /> : <InicioPersonal nombre={nombre} />
}

function AccesosStaff() {
  const { user } = useAuth()
  if (!user?.es_staff) return null
  return (
    <>
      <Boton variante="outlined" icono={Users} to="/app/usuarios">Usuarios</Boton>
      <Boton variante="outlined" icono={Plug} to="/app/integraciones">Integraciones</Boton>
    </>
  )
}

/** Cómo va la revisión del inventario y qué datos quedan por limpiar. */
function InicioInventario({ nombre }: { nombre?: string }) {
  const resumen = useResumen()
  const descargar = useDescargarPlanilla()

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera pagina__cabecera--con-accion">
        <div>
          <h1 className="dcc-t-heading-m">Hola, {nombre}</h1>
          <p className="texto-secundario">Revisión del inventario físico del DCC, oficina por oficina.</p>
        </div>
        <Boton icono={DoorOpen} to="/app/oficinas">Ir a oficinas</Boton>
      </header>

      {resumen.isLoading && <Cargando />}
      {resumen.isError && <Alerta tipo="error">{mensajeDeError(resumen.error)}</Alerta>}
      {resumen.data && (
        <>
          {resumen.data.solicitudes_pendientes > 0 && (
            <div className="aviso-accion">
              <Alerta tipo="info" titulo={`${numero(resumen.data.solicitudes_pendientes)} ${resumen.data.solicitudes_pendientes === 1 ? 'traslado pedido' : 'traslados pedidos'} por resolver`}>
                Al aprobarlos, los bienes se trasladan y queda en el historial.
              </Alerta>
              <Boton icono={ArrowRightLeft} variante="outlined" to="/app/traslados">Revisar traslados</Boton>
            </div>
          )}
          <section className="cifras" aria-label="Avance de la revisión">
            <div className="cifra cifra--exito">
              <span className="cifra__rotulo">Planchetas actualizadas</span>
              <b className="cifra__numero">
                {numero(resumen.data.planchetas.ACTUALIZADA)} <small>de {numero(resumen.data.recintos)}</small>
              </b>
              <progress className="cifra__avance" max={resumen.data.recintos || 1} value={resumen.data.planchetas.ACTUALIZADA} aria-label="Avance" />
            </div>
            <div className="cifra cifra--aviso">
              <span className="cifra__rotulo">{PLANCHETA.CON_MODIFICACIONES.texto}</span>
              <b className="cifra__numero">{numero(resumen.data.planchetas.CON_MODIFICACIONES)}</b>
              <span className="cifra__bajada">revisadas, falta imprimir</span>
            </div>
            <div className="cifra cifra--error">
              <span className="cifra__rotulo">{PLANCHETA.DESACTUALIZADA.texto}s</span>
              <b className="cifra__numero">{numero(resumen.data.planchetas.DESACTUALIZADA)}</b>
              <span className="cifra__bajada">por revisar</span>
            </div>
            <div className="cifra">
              <span className="cifra__rotulo">Bienes</span>
              <b className="cifra__numero">{numero(resumen.data.bienes)}</b>
              <span className="cifra__bajada">
                {(['INVENTARIABLE', 'NO_INVENTARIABLE', 'OTRO'] as const).map((t) => `${numero(resumen.data.tipos[t] ?? 0)} ${TIPO[t].corto.toLowerCase()}`).join(' · ')}
              </span>
            </div>
          </section>

          <div className="grilla">
            <Panel titulo="Datos por revisar">
              <ul className="lista-simple lista-revisar">
                {(Object.keys(REVISAR) as Revisar[]).map((clave) => (
                  <li key={clave}>
                    <Link to={`/app/bienes?revisar=${clave}`}>{REVISAR[clave]}</Link>
                    <span className="lista-revisar__n">
                      {numero(resumen.data.revisar[clave])}
                      {resumen.data.revisar[clave] > 0 && (
                        <Boton
                          variante="text"
                          tam="sm"
                          icono={Download}
                          aria-label={`Descargar planilla: ${REVISAR[clave]}`}
                          title="Descargar planilla para corregirlos en Excel"
                          onClick={() => descargar.mutate({ revisar: clave })}
                        />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel titulo="Accesos">
              <div className="pila">
                <Boton variante="outlined" icono={Package} to="/app/bienes">Todos los bienes</Boton>
                <Boton variante="outlined" icono={FileSpreadsheet} to="/app/bienes/planilla">Cargar o editar con planilla</Boton>
                <Boton variante="outlined" icono={ArrowRightLeft} to="/app/traslados">Traslados pedidos</Boton>
                <Boton variante="outlined" icono={Archive} to="/app/bajas">Bajas</Boton>
                <Boton variante="outlined" icono={Laptop} to="/app/teletrabajo">Teletrabajo y guías de despacho</Boton>
                <AccesosStaff />
              </div>
            </Panel>
          </div>
        </>
      )}
    </div>
  )
}

/** Para quien no administra bienes: sus traslados pedidos. */
function InicioPersonal({ nombre }: { nombre?: string }) {
  const { user, puede } = useAuth()
  const pideTraslados = puede('solicita_traslados')
  const solicitudes = useSolicitudes('PENDIENTE', pideTraslados)

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera">
        <h1 className="dcc-t-heading-m">Hola, {nombre}</h1>
        <p className="texto-secundario">Lo que pediste y en qué va.</p>
      </header>

      {!pideTraslados && !user?.es_staff && (
        <Alerta tipo="info" titulo="Tu cuenta todavía no tiene roles">Pídele a quien administra la aplicación que te asigne los que necesitas.</Alerta>
      )}

      <div className="grilla">
        {pideTraslados && (
          <Panel titulo="Traslados">
            <div className="pila">
              <p className="texto-secundario">
                Busca el bien (o entra a su oficina) y usa «Solicitar traslado». Quien administra los bienes lo aprueba y entonces se traslada.
              </p>
              {solicitudes.data && (
                <p>
                  <b>{numero(solicitudes.data.length)}</b> {solicitudes.data.length === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'}
                </p>
              )}
              <div className="acciones-pagina">
                <Boton icono={Package} to="/app/bienes">Buscar un bien</Boton>
                <Boton variante="outlined" icono={ArrowRightLeft} to="/app/traslados">Mis solicitudes</Boton>
              </div>
            </div>
          </Panel>
        )}
        {user?.es_staff && (
          <Panel titulo="Administración">
            <div className="pila">
              <AccesosStaff />
            </div>
          </Panel>
        )}
      </div>
    </div>
  )
}
